/**
 * Location-pin SDF icon for annotation markers (ADR-034 batch 4, #2).
 *
 * Replaces the plain circle marker with a map **location pin** — a teardrop
 * (round head + a tip at the bottom). Baked white-on-transparent and registered
 * with ``addImage(id, …, { sdf: true })`` so the marker symbol layer tints it via
 * ``icon-color`` (the operator's marker colour) and places its **tip on the
 * marker point** (``icon-anchor: "bottom"``). Same SDF recipe as the
 * marker-glyph icons (map/glyphs.ts). (Line arrowheads are no longer SDF symbols
 * — they're ground-geometry fill triangles; see annotation-map-features.ts.)
 *
 * Canvas acquisition is defensive (returns ``null`` + logs once on a headless /
 * jsdom backend rather than throwing — CLAUDE.md §8.1). WebGL-verified in the
 * §5.11 visual pass.
 *
 * Cross-references:
 *   - ADR-034 batch 4 (#2); feature spec §6.4.
 */
import type { Map as MapLibreMap, StyleImageMetadata } from "maplibre-gl";

/** The MapLibre image id for the marker location pin. */
export const MARKER_PIN_ICON_ID = "meridian-annotation-marker-pin";

/** Bake size in source px (``icon-size`` scales it on screen). */
const PIN_SDF_SIZE_PX = 48;

/** A square canvas of side ``size``; ``OffscreenCanvas`` preferred, else a
 *  detached ``<canvas>``; ``null`` when no backend (pure Node / jsdom). */
function createPinCanvas(size: number): OffscreenCanvas | HTMLCanvasElement | null {
  if (typeof OffscreenCanvas !== "undefined") {
    return new OffscreenCanvas(size, size);
  }
  if (typeof document !== "undefined") {
    const el = document.createElement("canvas");
    el.width = size;
    el.height = size;
    return el;
  }
  return null;
}

/**
 * Bake the location pin (white teardrop, transparent field, **tip at the
 * bottom**) into ``ImageData`` for ``addImage(…, { sdf: true })``. Returns
 * ``null`` when no 2D context is obtainable (headless backend) so the caller can
 * skip-and-log.
 */
export function bakeMarkerPinSdf(): ImageData | null {
  const size = PIN_SDF_SIZE_PX;
  const canvas = createPinCanvas(size);
  if (canvas === null) {
    return null;
  }
  let ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null;
  try {
    ctx = canvas.getContext("2d") as
      CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null;
  } catch {
    return null;
  }
  if (ctx === null) {
    return null;
  }
  ctx.clearRect(0, 0, size, size);
  ctx.fillStyle = "#ffffff";
  const cx = size / 2;
  const headCY = size * 0.36; // the round head centre (upper third)
  const headR = size * 0.28;
  const tipY = size * 0.97; // the tip near the bottom edge (icon-anchor "bottom")
  // The round head.
  ctx.beginPath();
  ctx.arc(cx, headCY, headR, 0, Math.PI * 2);
  ctx.fill();
  // The tail — from the head's lower flanks down to the tip (overlaps the head
  // for a smooth teardrop).
  ctx.beginPath();
  ctx.moveTo(cx - headR * 0.78, headCY + headR * 0.6);
  ctx.lineTo(cx + headR * 0.78, headCY + headR * 0.6);
  ctx.lineTo(cx, tipY);
  ctx.closePath();
  ctx.fill();
  return ctx.getImageData(0, 0, size, size);
}

/**
 * Register the marker pin SDF icon on the live map (idempotent — skips it when
 * already present via ``hasImage``, so safe on every ``styledata`` / re-attach).
 * Returns whether the icon is present after the call (for diagnostics / tests).
 */
export function registerMarkerIcon(map: MapLibreMap): boolean {
  if (map.hasImage(MARKER_PIN_ICON_ID)) {
    return true;
  }
  const image = bakeMarkerPinSdf();
  if (image === null) {
    console.warn(
      "[meridian/map/annotations] no 2D canvas context; skipped marker-pin SDF registration",
    );
    return false;
  }
  const meta: StyleImageMetadata = { sdf: true, pixelRatio: 1 };
  map.addImage(MARKER_PIN_ICON_ID, image, meta);
  return true;
}
